import { Component, OnInit, viewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { RegistrationService } from '../../shared/services/api/registration/registration.service';
import { NgxTurnstileComponent } from 'ngx-turnstile';
import { environment } from 'src/environments/environment';
import { AuthenticationService } from '../../shared/services/api/authentication/authentication.service';

@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.scss'],
})
export class RegisterComponent implements OnInit {
  protected readonly environment = environment;

  turnstileComponent = viewChild('turnstileComponent', { read: NgxTurnstileComponent });

  registerForm!: FormGroup;
  loading = false;
  submitted = false;
  errorMessage = '';
  showLoginLink = false;
  socialError = false;
  readonly cguUrl = 'https://s3-ao-writer.s3.eu-west-3.amazonaws.com/cgsu_explain_20260310.pdf';
  captchaResetTimer?: NodeJS.Timeout;

  constructor(
    private formBuilder: FormBuilder,
    private router: Router,
    private registrationService: RegistrationService,
    private authService: AuthenticationService
  ) {}

  ngOnInit(): void {
    this.registerForm = this.formBuilder.group({
      email: ['', [Validators.required, Validators.email, Validators.minLength(5)]],
      captcha: ['', [Validators.required]],
    });
  }

  get f() {
    return this.registerForm.controls;
  }

  onInputChange(): void {
    if (this.errorMessage || this.submitted) {
      this.errorMessage = '';
      this.showLoginLink = false;
      this.submitted = false;
    }
  }

  async onSubmit() {
    this.submitted = true;
    this.errorMessage = '';
    this.showLoginLink = false;

    if (this.registerForm.invalid) {
      return;
    }

    this.loading = true;

    try {
      await firstValueFrom(this.registrationService.register(this.f['email'].value, this.f['captcha'].value));
      await this.router.navigate(['/register/verify-email'], {
        state: { email: this.f['email'].value.trim().toLowerCase() },
      });
    } catch (error) {
      const err = error as { status?: number; error?: { message?: string; user_id?: number } };
      if (err?.status === 409) {
        this.errorMessage = 'register.error-already-exists';
        this.showLoginLink = true;
      } else if (err?.status === 429) {
        this.errorMessage = 'register.error-too-many-requests';
      } else {
        this.errorMessage = 'register.error-generic';
      }
      this.registerForm.controls['captcha'].setValue('');
      this.turnstileComponent()?.reset();
    } finally {
      this.loading = false;
    }
  }

  handleCaptcha(token: string | null) {
    if (token) {
      this.registerForm.controls['captcha'].setValue(token);
      this.runCaptchaResetTimer();
    }
  }

  handleError() {
    this.errorMessage = 'register.error-load';
    setTimeout(() => {
      this.errorMessage = '';
      this.turnstileComponent()?.reset();
    }, 5000);
  }

  runCaptchaResetTimer() {
    clearTimeout(this.captchaResetTimer);
    this.captchaResetTimer = setTimeout(() => this.turnstileComponent()?.reset(), 60000 * 4.5);
  }

  async socialSignin(provider: 'google' | 'microsoft') {
    this.socialError = false;
    try {
      const result = await this.authService.socialSignin(provider);
      window.location.href = result.authorization_url;
    } catch {
      this.socialError = true;
    }
  }
}
